import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";

// Retours & échanges + avoirs — clicoo ReturnController (/api/returns/*) and
// CreditNoteController (/api/credit-notes/*).

export const REFUND_MODES = [
    {key: "cash", label: "Espèces", description: "L'argent sort du tiroir-caisse."},
    {key: "original_payment", label: "Mode de paiement d'origine", description: "Carte, chèque… du ticket d'origine."},
    {key: "credit_note", label: "Avoir", description: "Bon d'achat imprimé avec code-barres, utilisable plus tard (en ligne)."},
    {key: "loyalty_balance", label: "Crédit carte fidélité", description: "Montant crédité en DH sur la carte du client (en ligne)."},
];

export const SALE_TYPE_LABELS = {
    return: "Retour",
    exchange: "Échange",
};

export const CREDIT_NOTE_STATUS_LABELS = {
    active: "Actif",
    used: "Utilisé",
    expired: "Expiré",
    cancelled: "Annulé",
};

export const CREDIT_NOTE_STATUS_CLASSES = {
    active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    used: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    expired: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
    cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

// Same field look as the catalogue forms
export const inputClass =
    "w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-60";

export const formatMoney = (value) =>
    `${(Number(value) || 0).toLocaleString("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2})} DH`;

export const formatDateTime = (value) => {
    if (!value) return "—";
    // API dates are either "Y-m-d H:i:s" (Casablanca wall clock) or ATOM.
    const date = new Date(value.includes("T") ? value : value.replace(" ", "T"));
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("fr-FR", {dateStyle: "short", timeStyle: "short"});
};

export const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value.includes("T") ? value : value.replace(" ", "T"));
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("fr-FR");
};

// "Y-m-d" in local time (react-date-range / <input type="date"> values).
export const toDayString = (date) => {
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const headers = async () => {
    const account = await secureStorage.getItem("selectedAccount");
    return {"X-account-id": account?.id, Accept: "application/json"};
};

// Error message sent by the API ({error}), or a generic fallback.
export const apiErrorMessage = (error, fallback) =>
    error?.response?.data?.error || error?.response?.data?.detail || fallback;

const query = (params) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") search.set(key, value);
    });
    return search.toString();
};

// ── Settings & reasons ──────────────────────────────────────────────────────

export const fetchReturnSettings = async () => {
    const {data} = await api.get("/returns/settings", {headers: await headers()});
    return data.settings;
};

export const updateReturnSettings = async (settings) => {
    const {data} = await api.put("/returns/settings", settings, {headers: await headers()});
    return data;
};

export const fetchReturnReasons = async () => {
    const {data} = await api.get("/returns/reasons", {headers: await headers()});
    return data;
};

export const createReturnReason = async (reason) => {
    const {data} = await api.post("/returns/reasons", reason, {headers: await headers()});
    return data;
};

export const updateReturnReason = async ({id, ...reason}) => {
    const {data} = await api.put(`/returns/reasons/${id}`, reason, {headers: await headers()});
    return data;
};

export const deleteReturnReason = async (id) => {
    await api.delete(`/returns/reasons/${id}`, {headers: await headers()});
};

// ── Returns ─────────────────────────────────────────────────────────────────

export const fetchReturns = async (params) => {
    const {data} = await api.get(`/returns?${query(params)}`, {headers: await headers()});
    return data;
};

export const fetchReturn = async (id) => {
    const {data} = await api.get(`/returns/${id}`, {headers: await headers()});
    return data;
};

export const fetchReturnsReport = async (params) => {
    const {data} = await api.get(`/returns/report?${query(params)}`, {headers: await headers()});
    return data;
};

// Employees of the account, for the list filter.
export const fetchEmployeeOptions = async () => {
    const {data} = await api.get("/employees", {headers: await headers()});
    const rows = Array.isArray(data) ? data : data?.items || data?.data || [];
    return rows.map((e) => ({id: e.id, name: e.name}));
};

// ── Credit notes ────────────────────────────────────────────────────────────

export const fetchCreditNotes = async (params) => {
    const {data} = await api.get(`/credit-notes?${query(params)}`, {headers: await headers()});
    return data;
};

export const fetchCreditNote = async (id) => {
    const {data} = await api.get(`/credit-notes/${id}`, {headers: await headers()});
    return data;
};

export const cancelCreditNote = async ({id, reason}) => {
    const {data} = await api.post(`/credit-notes/${id}/cancel`, {reason}, {headers: await headers()});
    return data;
};

// expiresAt: "Y-m-d" or null (never expires).
export const setCreditNoteExpiry = async ({id, expiresAt}) => {
    const {data} = await api.put(`/credit-notes/${id}/expiry`, {expires_at: expiresAt}, {headers: await headers()});
    return data;
};
